import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaDownload } from 'react-icons/fa';

const FloatingImage = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, delay: 0.5 }}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Floating Animation Container */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, -5, 0]
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'relative',
          width: '350px',
          height: '350px'
        }}
      >
        {/* Glowing Background */}
        <div
          style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
            background: 'linear-gradient(45deg, #3b82f6, #8b5cf6, #06b6d4)',
            borderRadius: '50%',
            filter: 'blur(20px)',
            opacity: 0.6,
            animation: 'pulse 3s ease-in-out infinite'
          }}
        />

        {/* Outer Frame */}
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '-10px',
            right: '-10px',
            bottom: '-10px',
            background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
            borderRadius: '50%',
            padding: '3px'
          }}
        >
          {/* Inner Frame */}
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #1f2937, #374151)',
              borderRadius: '50%',
              padding: '8px',
              boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Image Container */}
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
              }}
            >
              <img
                src="/image.jpeg"
                alt="Himanshu Mahata"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  filter: 'brightness(1.1) contrast(1.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              {/* Fallback if image doesn't load */}
              <div
                style={{
                  display: 'none',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}
              >
                HM
              </div>
            </div>
          </div>
        </div>

        {/* Floating Particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.sin(i) * 20, 0],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.5,
              ease: "easeInOut"
            }}
            style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: '#3b82f6',
              borderRadius: '50%',
              top: `${20 + i * 10}%`,
              left: `${10 + i * 15}%`,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

const Hero = () => {
  // Animated text loop state
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const animatedTexts = [
    "EXTENSION DEVELOPER",
    "FLUTTER DEVELOPER",
    "UNITY DEVELOPER"
  ];

  // Text animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) =>
        (prevIndex + 1) % animatedTexts.length
      );
    }, 4000); // Change text every 4 seconds (longer to read)

    return () => clearInterval(interval);
  }, [animatedTexts.length]);

  const socialLinks = [
    { icon: FaGithub, href: '#', label: 'GitHub' },
    { icon: FaLinkedin, href: '#', label: 'LinkedIn' },
    { icon: FaTwitter, href: '#', label: 'Twitter' },
  ];

  const heroStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #111827 100%)',
    paddingTop: '80px',
    paddingBottom: '2rem'
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem',
    display: 'grid',
    gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr',
    gap: window.innerWidth >= 768 ? '3rem' : '2rem',
    alignItems: 'center',
    position: 'relative',
    zIndex: 10
  };

  const backgroundStyle = {
    position: 'absolute',
    inset: 0,
    opacity: 0.2
  };

  return (
    <section id="home" style={heroStyle}>
      {/* Background Animation */}
      <div style={backgroundStyle}>
        <div style={{
          position: 'absolute',
          top: '25%',
          left: '25%',
          width: '24rem',
          height: '24rem',
          background: '#3b82f6',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'pulse 4s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '75%',
          right: '25%',
          width: '24rem',
          height: '24rem',
          background: '#8b5cf6',
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'pulse 4s ease-in-out infinite',
          animationDelay: '2s'
        }}></div>
      </div>

      <div style={containerStyle}>
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: window.innerWidth >= 1024 ? 'left' : 'center' }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            style={{
              fontSize: window.innerWidth >= 768 ? '3.75rem' : window.innerWidth >= 640 ? '2.5rem' : '2rem',
              fontWeight: 'bold',
              marginBottom: '1.5rem',
              lineHeight: 1.2
            }}
          >
            Hi, I'm{' '}
            <br />
            <span style={{
              position: 'relative',
              display: 'inline-block',
              height: '1.2em',
              overflow: 'hidden'
            }}>
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentTextIndex}
                  initial={{
                    opacity: 0,
                    y: 60,
                    scale: 0.8
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }}
                  exit={{
                    opacity: 0,
                    y: -60,
                    scale: 0.8
                  }}
                  transition={{
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    opacity: { duration: 0.6 },
                    scale: { duration: 0.6 }
                  }}
                  className="text-gradient"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    transformOrigin: 'left center'
                  }}
                >
                  {animatedTexts[currentTextIndex]}
                </motion.span>
              </AnimatePresence>
              {/* Invisible text to maintain layout width */}
              <span style={{
                opacity: 0,
                whiteSpace: 'nowrap',
                fontWeight: 'bold'
              }}>
                EXTENSION DEVELOPER
              </span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            style={{
              fontSize: '1.25rem',
              color: '#d1d5db',
              marginBottom: '2rem',
              lineHeight: 1.6,
              maxWidth: '600px',
              textAlign: window.innerWidth >= 1024 ? 'left' : 'center',
              margin: window.innerWidth >= 1024 ? '0 0 2rem 0' : '0 auto 2rem auto'
            }}
          >
            Passionate Flutter developer with 1 year of experience creating beautiful, cross-platform mobile applications. Skilled in modern web technologies, cloud deployment, and browser extension development.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            style={{
              display: 'flex',
              flexDirection: window.innerWidth >= 640 ? 'row' : 'column',
              gap: '1rem',
              marginBottom: '2rem',
              justifyContent: window.innerWidth >= 1024 ? 'flex-start' : 'center'
            }}
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary"
            >
              View My Work
            </motion.button>

            <motion.a
              href="/Himanshu_Mahata_CV.pdf"
              download="Himanshu_Mahata_CV.pdf"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                textDecoration: 'none'
              }}
            >
              <FaDownload />
              Download CV
            </motion.a>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            style={{
              display: 'flex',
              justifyContent: window.innerWidth >= 1024 ? 'flex-start' : 'center',
              gap: '1.5rem'
            }}
          >
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.href}
                whileHover={{ scale: 1.2, y: -5 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  fontSize: '1.5rem',
                  color: '#9ca3af',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                aria-label={social.label}
              >
                <social.icon />
              </motion.a>
            ))}
          </motion.div>
        </motion.div>

        {/* Right Content - Floating Image */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            height: window.innerWidth >= 1024 ? '500px' : '400px',
            width: '100%'
          }}
        >
          <FloatingImage />
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            width: '1.5rem',
            height: '2.5rem',
            border: '2px solid #9ca3af',
            borderRadius: '1rem',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              width: '0.25rem',
              height: '0.75rem',
              background: '#9ca3af',
              borderRadius: '0.125rem',
              marginTop: '0.5rem'
            }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
